import { createContext, useContext, useState, ReactNode, useEffect, useRef, useMemo } from 'react';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import useCurrencies from '@/shared/data/useCurrencies';
import useUserDetails from '@/shared/data/useUserDetails';

interface CurrencyContextValue {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

/**
 * sets the default selected currency based on the user's taker markets
 * by rolling up eligible + awarded invoice amounts (both pre-converted to USD)
 * and selecting the currency with the highest total included invoice amount
 * defaults to setting selected currency to USD
 */
export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const hasInitialized = useRef(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const currencies = useCurrencies();
  const { data: preferredAggregateCurrencyFormat } = useUserDetails(
    (data) => data.me?.formatPreferences?.aggregateCurrencyFormat
  );

  const { data: highestInvoiceAmountCurrency } = useTakerMarkets((takerMarkets) => {
    // rollup included (eligible + awarded) invoice amounts by currency (both already converted to USD)
    const totalByCurrency = takerMarkets.reduce<Record<string, number>>((acc, takerMarket) => {
      acc[takerMarket.currency] =
        (acc[takerMarket.currency] ?? 0) + (takerMarket.eligibleInvoiceAmountUsd + takerMarket.awardedInvoiceAmountUsd);
      return acc;
    }, {});

    // Convert currency totals to array format: [['MXN', 0], ['USD', 1500], ...]
    const currencyEntries = Object.entries(totalByCurrency);

    // get highest currency by total included invoice amount
    const [highestCurrency] = currencyEntries.reduce(
      ([maxCurrency, maxAmount], [currency, amount]) => {
        return amount > maxAmount ? [currency, amount] : [maxCurrency, maxAmount];
      },
      currencyEntries[0] ?? ['USD', 0] // Start with first currency if available and fallback to USD
    );

    return highestCurrency;
  });

  // set the default user selected currency to either the preferred aggregate currency
  // or the currency with the highest total included invoice amount
  // if preferred currency is not in the list of available currencies, default to highest included amount currency
  const defaultCurrency =
    preferredAggregateCurrencyFormat && currencies.includes(preferredAggregateCurrencyFormat)
      ? preferredAggregateCurrencyFormat
      : highestInvoiceAmountCurrency;

  useEffect(() => {
    if (defaultCurrency && !hasInitialized.current) {
      setSelectedCurrency(defaultCurrency);
      hasInitialized.current = true;
    }
  }, [defaultCurrency]);

  const contextValue = useMemo(() => {
    return { selectedCurrency, setSelectedCurrency };
  }, [selectedCurrency]);

  return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
};

export const useCurrencyContext = () => {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error('useCurrencyContext must be used within a CurrencyProvider');
  }

  return context;
};
